import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Agent,
  AgentThread,
  AgentMessage,
  GetThreadResponse,
  GetAgentThreadsResponse,
  GetAgentsResponse,
  MiniThreadMatch,
} from '@dreamai/types';
import { ThreadProcessingStatus } from '@dreamai/types';
import api from '../../../api';
import ThreadView from './ThreadView';
import ThreadViewLoading from './ThreadViewLoading';
import MessageSendErrorDisplay from './errors/MessageSendError';
import {
  MessageSendError,
  MessageSendErrorType,
} from './errors/conversationErrors';
import {
  useThreadActions,
  AsyncToolProgress,
} from '../../../contexts/ThreadLoadingContext';
import { ChatCanvasProvider } from '../../../contexts/ChatCanvasContext';
import { useAgent } from '../../../contexts/AgentContext';

interface ThreadViewContainerProps {
  threadId: string | null;
  agentId?: string;
  agentName?: string;
  predominantColour?: string;
  onMessageSent?: (threadId: string, responseMessages?: AgentMessage[]) => void;
  onThreadCreated?: (threadId: string, agentId: string) => void;
  onThreadDataLoaded?: (threadData: {
    isMainThread: boolean;
    isInternalMonologue?: boolean;
    viaShared?: boolean;
    viaCompany?: boolean;
    sharePermission?: string;
    hasShares?: boolean;
    discussingObject?: string;
    discussingObjectMatch?: MiniThreadMatch;
  }) => void;
  onDescriptionChange?: (description: string) => void;
  creationMode?: boolean;
  recoveryMessage?: string;
  onRecoveryMessageUsed?: (message?: string) => void;
  inMiniMode?: boolean;
  discussingObject?: string;
  discussingObjectMatch?: MiniThreadMatch;
  hasWritePermission?: boolean;
  onOpenCanvas?: () => void;
  readOnly?: boolean;
  obscured?: boolean;
}

const ThreadViewContainer: React.FC<ThreadViewContainerProps> = ({
  threadId,
  agentId,
  agentName,
  predominantColour,
  onMessageSent,
  onThreadCreated,
  onThreadDataLoaded,
  onDescriptionChange,
  creationMode = false,
  recoveryMessage,
  onRecoveryMessageUsed,
  inMiniMode = false,
  discussingObject,
  discussingObjectMatch,
  hasWritePermission = true,
  onOpenCanvas,
  readOnly = false,
  obscured = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { setThreadLoading, setAsyncTools, registerThread } =
    useThreadActions();
  const { setAgents } = useAgent();

  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationState, setPaginationState] = useState<{
    hasMore: boolean;
    total: number;
    offset: number;
    loading: boolean;
  }>({
    hasMore: false,
    total: 0,
    offset: 0,
    loading: false,
  });
  const [messageSendError, setMessageSendError] =
    useState<MessageSendError | null>(null);
  const [isMainThread, setIsMainThread] = useState<boolean>(false);
  const [isAgentLedProject, setIsAgentLedProject] = useState<boolean>(false);
  const [isArchivedThread, setIsArchivedThread] = useState<boolean>(false);
  const [isSubThread, setIsSubThread] = useState<boolean>(false);
  const [subThreads, setSubThreads] = useState<
    Array<{
      id: string;
      description: string | null;
      created_at: string;
      last_updated_at: string;
      fork_message_timestamp?: string;
      fork_message_content?: string | null;
    }>
  >([]);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [agentLoading, setAgentLoading] = useState<boolean>(false);
  const [cipherSalt, setCipherSalt] = useState<string | null>(null);
  // Track when the thread's actual agent differs from the prop agentId
  // (e.g., ops worker viewing a human task thread belonging to a different PA)
  const [threadAgentOverride, setThreadAgentOverride] =
    useState<boolean>(false);

  // Function to fetch agent data
  const fetchAgent = async (agentId: string) => {
    // Skip fetching if agentId is 'other' - this is a placeholder for shared threads
    if (agentId === 'other') {
      return;
    }

    try {
      setAgentLoading(true);
      const token = await getAccessTokenSilently();

      // First try to get all agents
      const response = await api.get<GetAgentsResponse>('/agents', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.agents) {
        const foundAgent = response.data.agents.find((a) => a.id === agentId);
        if (foundAgent) {
          setAgent(foundAgent);
          // Update the AgentContext with all agents
          setAgents(response.data.agents);
          return;
        }

        // Agent not found in general list - try specific endpoint with access control
        try {
          const specificResponse = await api.get<GetAgentsResponse>(
            `/agents/${agentId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (
            specificResponse.data.success &&
            specificResponse.data.agents &&
            specificResponse.data.agents.length > 0
          ) {
            const fetchedAgent = specificResponse.data.agents[0];
            setAgent(fetchedAgent);
            // Add to AgentContext so DreamieDisplay can find it
            setAgents((prevAgents) => {
              const exists = prevAgents.some((a) => a.id === fetchedAgent.id);
              return exists ? prevAgents : [...prevAgents, fetchedAgent];
            });
          }
        } catch (specificError) {
          console.error('Error fetching specific agent:', specificError);
          // Agent not accessible or doesn't exist
        }
      }
    } catch (error) {
      console.error('Error fetching agent data:', error);
    } finally {
      setAgentLoading(false);
    }
  };

  // Helper function to check thread processing status and update loading state
  const checkProcessingStatus = (
    threadId: string,
    processingStatus: string,
  ) => {
    if (processingStatus === ThreadProcessingStatus.PROCESSING) {
      setThreadLoading(threadId, true);
    } else {
      setThreadLoading(threadId, false);
    }
  };

  // Helper function to process async tools from a thread response
  const processAsyncTools = (threadId: string, asyncToolCalls: any[]) => {
    // Log the async tool calls
    console.log('Found async tool calls in thread response:', asyncToolCalls);

    // Filter for only active async tools (not completed or errored)
    const activeTools = asyncToolCalls.filter(
      (tool) =>
        tool.status !== 'COMPLETED' &&
        tool.status !== 'ERROR' &&
        tool.status !== 'ABORTED',
    );

    if (activeTools.length > 0) {
      // Set the thread loading state to true
      setThreadLoading(threadId, true);

      // Add each active async tool to the ThreadLoadingContext
      const asyncToolsMap: Record<string, AsyncToolProgress> = {};

      activeTools.forEach((tool) => {
        if (tool.id) {
          asyncToolsMap[tool.id] = {
            tool_id: tool.id,
            threadId,
            progress: tool.progress || 0,
            status: tool.status || 'STARTED',
            name: tool.name || tool.tool || tool.id,
          };
        }
      });

      // Update the async tools in the context
      if (Object.keys(asyncToolsMap).length > 0) {
        setAsyncTools((prev) => ({
          ...prev,
          ...asyncToolsMap,
        }));
      }
    }
  };

  // Function to fetch thread data to get is_main_thread property and other metadata
  const fetchThreadData = async (
    threadId: string,
  ): Promise<{
    thread: AgentThread | null;
    hasShares: boolean;
    viaShared?: boolean;
    viaCompany?: boolean;
    sharePermission?: string;
    subThreads?: Array<{
      id: string;
      description: string | null;
      created_at: string;
      last_updated_at: string;
      fork_message_timestamp?: string;
      fork_message_content?: string | null;
    }>;
  }> => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<
        import('@dreamai/types').GetThreadDetailsResponse
      >(`/agents/threads/${threadId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.thread) {
        return {
          thread: response.data.thread,
          hasShares: response.data.has_shares || false,
          viaShared: response.data.via_shared,
          viaCompany: response.data.via_company,
          sharePermission: response.data.share_permission,
          subThreads: (response.data as any).sub_threads,
        };
      }
      return {
        thread: null,
        hasShares: false,
        viaShared: undefined,
        viaCompany: undefined,
        sharePermission: undefined,
        subThreads: undefined,
      };
    } catch (error) {
      console.error('Error fetching thread data:', error);
      return {
        thread: null,
        hasShares: false,
        viaShared: undefined,
        viaCompany: undefined,
        sharePermission: undefined,
        subThreads: undefined,
      };
    }
  };

  // Fetch thread messages (original function for compatibility)
  const fetchThreadMessages = async (threadId: string) => {
    await fetchMessagesWithPagination(threadId);
  };

  // Internal function to fetch messages with pagination support
  const fetchMessagesWithPagination = useCallback(
    async (
      threadId: string,
      options?: { offset?: number; limit?: number; append?: boolean },
    ) => {
      try {
        console.log('Loading Messages', options);

        // Only show loading state for initial loads, not pagination
        if (!options?.append) {
          setMessagesLoading(true);
        }
        setError(null);
        const token = await getAccessTokenSilently();

        // Build query parameters for pagination and obscured mode
        const params = new URLSearchParams();
        if (options?.offset !== undefined) {
          params.append('offset', options.offset.toString());
        }
        if (options?.limit !== undefined) {
          params.append('limit', options.limit.toString());
        }
        if (obscured) {
          params.append('obscured', 'true');
          // Pass existing salt for consistent ciphering across pagination
          if (cipherSalt) {
            params.append('salt', cipherSalt);
          }
        }
        // Forward im param so backend includes internal messages in debug mode
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('im') === 'true') {
          params.append('im', 'true');
        }

        const queryString = params.toString();
        const url = `/agents/thread/${threadId}${queryString ? `?${queryString}` : ''}`;

        const response = await api.get<GetThreadResponse>(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success && response.data.thread) {
          const thread = response.data.thread;

          // Capture cipher salt if present (for obscured mode)
          const responseData = response.data as GetThreadResponse & {
            cipherSalt?: string;
          };
          if (responseData.cipherSalt) {
            setCipherSalt(responseData.cipherSalt);
          }

          if (options?.append) {
            // Append new messages to existing ones (for pagination)
            setMessages((prevMessages) => [
              ...thread.messages,
              ...prevMessages,
            ]);
          } else {
            // Replace messages (initial load)
            setMessages(thread.messages);
          }

          // Register thread for streaming subscription
          registerThread(threadId);

          // If the thread's actual agent differs from the prop agentId, fetch the correct agent.
          // This handles: (1) shared threads with agentId='other', (2) human task threads where
          // the ops worker's own PA is selected but the thread belongs to a different PA.
          if (
            thread.agentId &&
            (!agentId || agentId === 'other' || agentId !== thread.agentId)
          ) {
            await fetchAgent(thread.agentId);
            if (agentId && agentId !== 'other' && agentId !== thread.agentId) {
              setThreadAgentOverride(true);
            }
          }

          // Check if this thread is a main thread
          const {
            thread: threadData,
            hasShares,
            viaShared,
            viaCompany,
            sharePermission,
            subThreads: fetchedSubThreads,
          } = await fetchThreadData(threadId);
          setSubThreads(fetchedSubThreads || []);
          const isMainThread = !!threadData?.is_main_thread;
          setIsMainThread(isMainThread);
          setIsArchivedThread(!!threadData?.is_archived_thread);
          setIsSubThread(!!threadData?.parent_thread_id);

          // Extract discussing object info (use first one if multiple)
          const firstDiscussingObject = threadData?.discussing_objects?.[0];

          // Agent-led projects carry an 'AgentLedProject' marker alongside the
          // primary 'Project' object. Used to render the tap-to-start kickoff
          // call button under the agent's first message.
          setIsAgentLedProject(
            !!threadData?.discussing_objects?.some(
              (o) => o.discussing_object === 'AgentLedProject',
            ),
          );

          // Notify parent component about thread data
          if (onThreadDataLoaded) {
            onThreadDataLoaded({
              isMainThread,
              isInternalMonologue: threadData?.is_internal_monologue,
              viaShared,
              viaCompany,
              sharePermission,
              hasShares,
              discussingObject: firstDiscussingObject?.discussing_object,
              discussingObjectMatch:
                firstDiscussingObject?.discussing_object_match,
            });
          }

          // Notify parent component about thread description
          if (onDescriptionChange && threadData?.description) {
            onDescriptionChange(threadData.description);
          }

          // Check if the thread is in PROCESSING state
          if (thread.processingStatus) {
            checkProcessingStatus(threadId, thread.processingStatus);
          }

          // Process any async tools in the thread response
          if (thread.asyncToolCalls && Array.isArray(thread.asyncToolCalls)) {
            processAsyncTools(threadId, thread.asyncToolCalls);
          }

          // Update pagination state with the response metadata
          if (response.data.pagination) {
            setPaginationState({
              hasMore: response.data.pagination.hasMore,
              total: response.data.pagination.total,
              offset: options?.append
                ? paginationState.offset + (options.limit || 20)
                : options?.limit || 20,
              loading: false,
            });
          } else {
            // If no pagination metadata, assume no more messages
            setPaginationState((prev) => ({
              ...prev,
              hasMore: false,
              loading: false,
            }));
          }

          // Return pagination metadata for the caller
          return response.data.pagination;
        } else {
          setError('Failed to load conversation messages');
        }
      } catch (err) {
        setError('Error loading conversation messages');
        console.error('Error fetching thread messages:', err);
      } finally {
        setMessagesLoading(false);
      }
    },
    [getAccessTokenSilently, obscured, cipherSalt],
  );

  // Function to load more messages for pagination
  const loadMoreMessages = useCallback(
    async (threadId: string, offset: number, limit: number = 20) => {
      if (paginationState.loading) return;

      setPaginationState((prev) => ({ ...prev, loading: true }));

      try {
        return await fetchMessagesWithPagination(threadId, {
          offset,
          limit,
          append: true,
        });
      } catch (error) {
        setPaginationState((prev) => ({ ...prev, loading: false }));
        throw error;
      }
    },
    [fetchMessagesWithPagination, paginationState.loading],
  );

  // Handle message sent from ThreadView
  const handleMessageSent = (
    threadId: string,
    responseMessages?: AgentMessage[],
  ) => {
    // Call parent callback if provided
    if (onMessageSent) {
      onMessageSent(threadId, responseMessages);
    }

    // If this is a new conversation, notify parent about thread creation
    if (creationMode && onThreadCreated) {
      onThreadCreated(threadId, agentId || '');
    }

    // If we have response messages, add them directly to the messages array
    if (responseMessages && responseMessages.length > 0) {
      setMessages((prevMessages) => [...prevMessages, ...responseMessages]);
    }
  };

  // Handle message send error
  const handleMessageSendError = (
    errorType: MessageSendErrorType,
    errorMessage: string,
    failedMessage?: string,
  ) => {
    setMessageSendError({
      type: errorType,
      message: errorMessage,
      recoveryMessage: failedMessage,
    });
  };

  // Load agent data when agentId changes (skip if thread's actual agent overrides the prop)
  useEffect(() => {
    if (threadAgentOverride) return;
    if (agentId) {
      fetchAgent(agentId);
    } else {
      setAgent(null);
    }
  }, [agentId, threadAgentOverride]);

  // Load messages when threadId changes
  useEffect(() => {
    // Reset thread agent override when navigating to a different thread
    setThreadAgentOverride(false);
    if (threadId && !creationMode) {
      fetchThreadMessages(threadId);
    } else {
      setMessages([]);
      setIsMainThread(false);
      setIsArchivedThread(false);
    }
  }, [threadId, creationMode]);

  // Clear errors when threadId changes
  useEffect(() => {
    setError(null);
    setMessageSendError(null);
  }, [threadId]);

  // Show error state
  if (error) {
    return <div className='error-message'>{error}</div>;
  }

  // Show message send error
  if (messageSendError) {
    return (
      <MessageSendErrorDisplay
        errorType={messageSendError.type}
        errorMessage={messageSendError.message}
        recoveryMessage={messageSendError.recoveryMessage}
        onRetry={(message) => {
          if (onRecoveryMessageUsed) {
            onRecoveryMessageUsed(message);
          }
          setMessageSendError(null);
        }}
      />
    );
  }

  // Show loading state
  if (messagesLoading) {
    return <ThreadViewLoading />;
  }

  // Show ThreadView
  const threadViewComponent = (
    <ThreadView
      messages={messages}
      threadId={threadId}
      agentId={
        threadAgentOverride ? agent?.id || agentId : agentId || agent?.id
      }
      agentName={
        threadAgentOverride
          ? agent?.name || agentName
          : agentName || agent?.name
      }
      agentNickname={agent?.nickname}
      predominantColour={
        threadAgentOverride
          ? agent?.predominant_colour || predominantColour
          : predominantColour || agent?.predominant_colour
      }
      onMessageSent={handleMessageSent}
      onCreateError={handleMessageSendError}
      recoveryMessage={recoveryMessage}
      onRecoveryMessageUsed={onRecoveryMessageUsed}
      setMessages={setMessages}
      fetchThreadMessages={fetchThreadMessages}
      loadMoreMessages={loadMoreMessages}
      paginationState={paginationState}
      inMiniMode={inMiniMode}
      discussingObject={discussingObject}
      discussingObjectMatch={discussingObjectMatch}
      isAgentLedProject={isAgentLedProject}
      hasWritePermission={hasWritePermission}
      isArchivedThread={isArchivedThread}
      isSubThread={isSubThread}
      onOpenCanvas={onOpenCanvas}
      readOnly={readOnly}
      obscured={obscured}
      cipherSalt={cipherSalt}
      initialSubThreads={subThreads}
    />
  );

  // Wrap with ChatCanvasProvider if in mini mode
  if (inMiniMode) {
    return <ChatCanvasProvider>{threadViewComponent}</ChatCanvasProvider>;
  }

  return threadViewComponent;
};

export default ThreadViewContainer;
